import { useEffect, useRef } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  ImagePlus,
  Italic,
  Underline,
} from "lucide-react";
import InsertPlaceholderMenu from "@/components/placeholder/InsertPlaceholderMenu";
import { Button } from "@/components/ui/button";
import { GRD } from "@/constants/testIds";
import {
  REPORT_PLACEHOLDER_GROUPS,
  placeholderChipHtml,
  reportFieldToken,
} from "@/lib/genericReportPlaceholders";
import { cn } from "@/lib/utils";

const FONT_SIZES = [
  { value: "2", label: "Small" },
  { value: "3", label: "Normal" },
  { value: "4", label: "Medium" },
  { value: "5", label: "Large" },
  { value: "6", label: "X-Large" },
];

function ToolbarButton({ title, onClick, disabled, children, testId }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      title={title}
      data-testid={testId}
      disabled={disabled}
      className="h-7 w-7 text-slate-600"
      onMouseDown={(event) => {
        event.preventDefault();
        onClick?.();
      }}
    >
      {children}
    </Button>
  );
}

export default function ReportSectionEditor({
  value = "",
  onChange,
  disabled,
  surfaceClassName,
  testId,
  placeholder = "Type header content…",
  insertPlaceholderTestId,
  insertLogoTestId,
}) {
  const editorRef = useRef(null);
  const lastHtml = useRef("");
  const fileRef = useRef(null);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value !== lastHtml.current && el.innerHTML !== value) {
      el.innerHTML = value || "";
      lastHtml.current = value || "";
    }
  }, [value]);

  const emitHtml = () => {
    const html = editorRef.current?.innerHTML || "";
    lastHtml.current = html;
    onChange?.(html);
  };

  const run = (command, arg) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    emitHtml();
  };

  const insertHtml = (html) => {
    if (disabled) return;
    editorRef.current?.focus();
    const ok = document.execCommand("insertHTML", false, html);
    if (!ok) {
      const el = editorRef.current;
      if (el) {
        el.innerHTML = `${el.innerHTML}${html}`;
      }
    }
    emitHtml();
  };

  const insertPlaceholder = (token, label) => {
    insertHtml(`${placeholderChipHtml(token, label)}\u00a0`);
  };

  const onPickLogo = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > 200 * 1024) {
      window.alert("Logo files must be 200 KB or smaller for this experimental designer.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const src = String(reader.result || "");
      if (!src.startsWith("data:image/")) return;
      insertHtml(`<img class="grd-logo" alt="Practice logo" src="${src}" />`);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="grd-editor">
      <div className="grd-editor__toolbar">
        <ToolbarButton title="Bold" disabled={disabled} onClick={() => run("bold")}>
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Italic" disabled={disabled} onClick={() => run("italic")}>
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Underline" disabled={disabled} onClick={() => run("underline")}>
          <Underline className="h-3.5 w-3.5" />
        </ToolbarButton>
        <span className="grd-editor__sep" />
        <select
          className="grd-editor__select"
          aria-label="Font size"
          defaultValue="3"
          disabled={disabled}
          onMouseDown={(event) => event.stopPropagation()}
          onChange={(event) => run("fontSize", event.target.value)}
        >
          {FONT_SIZES.map((size) => (
            <option key={size.value} value={size.value}>
              {size.label}
            </option>
          ))}
        </select>
        <span className="grd-editor__sep" />
        <ToolbarButton title="Align left" disabled={disabled} onClick={() => run("justifyLeft")}>
          <AlignLeft className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Align center" disabled={disabled} onClick={() => run("justifyCenter")}>
          <AlignCenter className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Align right" disabled={disabled} onClick={() => run("justifyRight")}>
          <AlignRight className="h-3.5 w-3.5" />
        </ToolbarButton>
        <span className="grd-editor__sep" />
        <InsertPlaceholderMenu
          groups={REPORT_PLACEHOLDER_GROUPS}
          disabled={disabled}
          testId={insertPlaceholderTestId || GRD.insertPlaceholder}
          itemTestIdPrefix={testId}
          formatToken={reportFieldToken}
          onSelect={insertPlaceholder}
        />
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={insertLogoTestId || GRD.insertLogo}
          className="h-7 rounded-md px-2 text-xs"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => fileRef.current?.click()}
        >
          <ImagePlus className="h-3.5 w-3.5" />
          Insert Logo
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
          className="hidden"
          onChange={onPickLogo}
        />
      </div>
      <div
        ref={editorRef}
        data-testid={testId}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Report section content"
        spellCheck={false}
        className={cn("grd-editor__surface", surfaceClassName)}
        data-placeholder={placeholder}
        onInput={emitHtml}
        onBlur={emitHtml}
      />
    </div>
  );
}
